import { useEffect, useState } from 'react'
import { api, UnauthorizedError } from '../../api/client'
import { useAuth } from '../../hooks/useAuth'
import { useCounts } from '../../hooks/useCounts'
import { useToast } from '../../hooks/useToast'
import { PIPELINE_STATUSES, STATUS_LABEL } from '../../lib/format'
import type { PipelineItem, PipelineStatus } from '../../lib/types'

/**
 * The pipeline — the prototype's renderPipeline against the shared backend list.
 *
 * Status changes and removals are Researcher writes (PUT/DELETE /api/pipeline); viewers get
 * the same list read-only, mirroring how the dashboard hides Pursue from them. The item
 * embeds its opportunity summary server-side, so this view needs exactly one call.
 */
export function PipelineView() {
  const { can } = useAuth()
  const { refresh: refreshCounts } = useCounts()
  const toast = useToast()

  const [items, setItems] = useState<PipelineItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    api
      .pipeline()
      .then(setItems)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load the pipeline.'))
      .finally(() => setLoading(false))
  }, [])

  async function setStatus(id: string, status: PipelineStatus) {
    try {
      const updated = await api.setPipelineStatus(id, status)
      setItems((prev) => prev.map((p) => (p.id === id ? updated : p)))
      toast('Status updated')
    } catch (e) {
      if (!(e instanceof UnauthorizedError)) toast(e instanceof Error ? e.message : 'Update failed.')
    }
  }

  async function remove(id: string) {
    try {
      await api.removeFromPipeline(id)
      setItems((prev) => prev.filter((p) => p.id !== id))
      toast('Removed from pipeline')
      void refreshCounts()
    } catch (e) {
      if (!(e instanceof UnauthorizedError)) toast(e instanceof Error ? e.message : 'Remove failed.')
    }
  }

  const canEdit = can('RESEARCHER')

  return (
    <div className="view active">
      <div className="dash-header">
        <h1 className="dash-title">Pipeline</h1>
        <div className="dash-sub">Opportunities you are actively pursuing</div>
      </div>

      {error && (
        <div className="err" role="alert">
          {error}
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="empty">
          <div className="empty-icon">🚀</div>
          <div className="empty-title">Pipeline is empty</div>
          <div className="empty-sub">Hit Pursue on any opportunity to add it here</div>
        </div>
      )}

      {items.map((p) => (
        <div className="pipe-card" key={p.id}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="pipe-title">{p.title}</div>
            <div className="pipe-buyer">
              {p.buyer} · {p.value || 'TBC'}
            </div>
          </div>
          <span className={`pipe-status ${p.status}`}>{STATUS_LABEL[p.status] ?? p.status}</span>
          {canEdit && (
            <>
              <select
                value={p.status}
                aria-label={`Status for ${p.title}`}
                onChange={(e) => void setStatus(p.id, e.target.value as PipelineStatus)}
              >
                {PIPELINE_STATUSES.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
              <button
                className="btn-outline"
                style={{ padding: '5px 10px', color: 'var(--grey)' }}
                aria-label={`Remove ${p.title}`}
                onClick={() => void remove(p.id)}
              >
                ✕
              </button>
            </>
          )}
        </div>
      ))}
    </div>
  )
}
